/**
 * Constantes partagées entre client et serveur pour la recherche d'établissements
 * Source unique de vérité - ne pas dupliquer ces valeurs ailleurs
 */

import type { SejourSlug } from "./establishment-filters";
import type { LivingLocalityValue, LivingSurroundingsValue } from "./living-setting";

/**
 * Type de logement proposé aux voyageurs (chantier gîtes v4.0).
 * - `'chambre-hotes'` : établissement (chambre d'hôtes, art. L324-3).
 * - `'gite'` : meublé de tourisme loué en entier (art. L324-1).
 * Valeurs alignées sur le param `type` du schema de recherche.
 */
export type AccommodationType = 'chambre-hotes' | 'gite';

// Configuration de pagination et limites
export const SEARCH_FILTERS = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  MIN_PRICE: 0,
  MAX_PRICE: 2000,
  MIN_ADULTS: 1,
  MAX_ADULTS: 100,
  DEFAULT_ADULTS: 2,
  MIN_CHILDREN: 0,
  MAX_CHILDREN: 20,
  DEFAULT_CHILDREN: 0,
  MIN_CHILD_AGE: 0,
  MAX_CHILD_AGE: 17,
  DESCRIPTION_TRUNCATE_LENGTH: 220,
  DEFAULT_RADIUS_KM: 30,
  MIN_RADIUS_KM: 10,
  MAX_RADIUS_KM: 100,
  /** Paliers d'élargissement automatique du rayon quand 0 résultats (en km) */
  AUTO_EXPAND_STEPS: [50, 100] as readonly number[],
} as const;

/**
 * Seuil « grand groupe » (spec 4.107.0, portée réduite en 4.108.0 — s'appelait
 * alors `GUEST_STEPPER_THRESHOLD`).
 *
 * Il ne gouverne aucune bascule de saisie : le compteur voyageurs est un
 * contrôle unique et permanent `[−] [nombre éditable] [+]`
 * (`HoliGuestCounter`), identique à toutes les valeurs. Deux rôles :
 *
 * 1. **Âges des enfants** : à partir de ce seuil, le bloc d'âges disparaît et
 *    `childrenAges` n'est PAS envoyé (tableau vidé, jamais tronqué) — le
 *    `.refine()` du schema de recherche impose `childrenAges.length === children`
 *    dès que le tableau est fourni.
 * 2. **État vide grand groupe** de `search.vue` : message dédié quand une
 *    recherche à partir de ce seuil ne rend aucun résultat.
 */
export const LARGE_GROUP_THRESHOLD = 15;

/**
 * Tronque un texte à la frontière de mot la plus proche (en deçà de maxLength).
 * Le texte retourné est toujours <= maxLength caractères et ne coupe jamais
 * un mot en plein milieu. Pas d'ellipse ajoutée (laissée au rendu CSS).
 * Si le texte fait déjà <= maxLength, il est retourné tel quel.
 */
export function truncateAtWordBoundary(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const sliced = text.slice(0, maxLength);
  const lastSpace = sliced.lastIndexOf(" ");
  // Garde-fou : si aucun espace dans la fenêtre, on tronque brut (mot exotique très long).
  if (lastSpace <= 0) return sliced.trimEnd();
  return sliced.slice(0, lastSpace).trimEnd();
}

// Paliers du sélecteur "Élargir la zone" sur les pages POI /proche/[slug] (Lot 5).
// Source unique client/serveur : whitelist stricte acceptée par /api/search/near.
export const POI_EXPAND_RADII = [20, 30, 50] as const;
export type PoiExpandRadius = (typeof POI_EXPAND_RADII)[number];

// Nombre max d'établissements retournés par /api/search/near (aligné sur la page SSR /proche/[slug]).
export const POI_EXPAND_MAX_RESULTS = 30;

// Options de rayon de recherche (km)
export const RADIUS_OPTIONS = [
  { value: 10, label: "10 km" },
  { value: 20, label: "20 km" },
  { value: 30, label: "30 km" },
  { value: 50, label: "50 km" },
  { value: 100, label: "100 km" },
];

// Filtres rapides affichés par défaut (4-5 visibles)
export const QUICK_FILTERS = [
  { key: "hasTableDhotes", label: "Table d'hôtes", icon: "i-lucide-utensils" },
  { key: "petsAllowed", label: "Animaux acceptés", icon: "i-lucide-dog" },
  { key: "hasParking", label: "Parking", icon: "i-lucide-car" },
  { key: "hasPool", label: "Piscine", icon: "i-lucide-waves" },
  { key: "hasWifi", label: "Wifi", icon: "i-lucide-wifi" },
  { key: "hasBikeRental", label: "Location vélos", icon: "i-lucide-bike" },
  { key: "hasSauna", label: "Spa ou sauna", icon: "i-lucide-flame" },
  { key: "hasEVCharger", label: "Borne recharge VE", icon: "i-lucide-zap" },
  { key: "hasHorseFacilities", label: "Accueil chevaux", icon: "i-lucide-paw-print" },
] as const;

// Options de tri (mutable pour compatibilité avec USelect)
// Les labels sont des fallbacks — le frontend les remplace par les traductions i18n
export const SORT_OPTIONS = [
  { value: "price:asc", label: "Price asc" },
  { value: "price:desc", label: "Price desc" },
  { value: "googleRating:desc", label: "Best rated" },
  { value: "capacity:desc", label: "Capacity desc" },
  { value: "distance:asc", label: "Nearest" },
  { value: "createdAt:desc", label: "Newest" },
];

// Régions françaises pour autocomplétion statique (name + slug pour navigation PSEO)
export const REGIONS_FR = [
  { name: "Auvergne-Rhône-Alpes", slug: "auvergne-rhone-alpes" },
  { name: "Bourgogne-Franche-Comté", slug: "bourgogne-franche-comte" },
  { name: "Bretagne", slug: "bretagne" },
  { name: "Centre-Val de Loire", slug: "centre-val-de-loire" },
  { name: "Corse", slug: "corse" },
  { name: "Grand Est", slug: "grand-est" },
  { name: "Hauts-de-France", slug: "hauts-de-france" },
  { name: "Île-de-France", slug: "ile-de-france" },
  { name: "Normandie", slug: "normandie" },
  { name: "Nouvelle-Aquitaine", slug: "nouvelle-aquitaine" },
  { name: "Occitanie", slug: "occitanie" },
  { name: "Pays de la Loire", slug: "pays-de-la-loire" },
  { name: "Provence-Alpes-Côte d'Azur", slug: "provence-alpes-cote-dazur" },
  { name: "Guadeloupe", slug: "guadeloupe" },
  { name: "Martinique", slug: "martinique" },
  { name: "Guyane", slug: "guyane" },
  { name: "La Réunion", slug: "la-reunion" },
  { name: "Mayotte", slug: "mayotte" },
] as const;

// Types exportés
export type QuickFilterKey = (typeof QUICK_FILTERS)[number]["key"];
export type SortOptionValue =
  | "price:asc"
  | "price:desc"
  | "googleRating:desc"
  | "capacity:desc"
  | "distance:asc"
  | "createdAt:desc";
export type RegionFR = (typeof REGIONS_FR)[number]["name"];

// Interface pour les filtres de recherche
export interface SearchFilters {
  q?: string;
  /**
   * Type d'hébergement recherché (spec 4.28.0).
   * - `'all'` (ou undefined) : merge chambres d'hôtes + gîtes.
   * - `'chambre-hotes'` : chambres d'hôtes seules.
   * - `'gite'` : gîtes seuls.
   */
  type?: 'all' | AccommodationType;
  city?: string;
  region?: string;
  checkIn?: string;
  checkOut?: string;
  department?: string;
  adults?: number;
  children?: number;
  /** Âges des enfants (tableau d'entiers 0-17), longueur = children */
  childrenAges?: readonly number[] | number[];
  minAdults?: number;
  minChildren?: number;
  minPrice?: number;
  maxPrice?: number;
  radiusKm?: number;
  centerLat?: number;
  centerLng?: number;
  hasTableDhotes?: boolean;
  petsAllowed?: boolean;
  hasParking?: boolean;
  hasPool?: boolean;
  hasWifi?: boolean;
  hasBikeRental?: boolean;
  hasSauna?: boolean;
  hasEVCharger?: boolean;
  hasHorseFacilities?: boolean;
  /**
   * Séjours types (4.137.0), combinés en ET. Liste triée et dédoublonnée
   * (cf. `parseSejoursParam` dans `establishment-filters.ts`).
   */
  sejours?: readonly SejourSlug[] | SejourSlug[];
  /**
   * Cadre de vie (4.144.0) : OU dans l'axe, ET entre axes. Listes triées et
   * dédoublonnées (cf. `parseLivingLocalityParam` dans `living-setting.ts`).
   */
  livingLocality?: readonly LivingLocalityValue[] | LivingLocalityValue[];
  livingSurroundings?: readonly LivingSurroundingsValue[] | LivingSurroundingsValue[];
  page?: number;
  limit?: number;
  sortBy?: "price" | "capacity" | "createdAt" | "distance" | "googleRating";
  sortOrder?: "asc" | "desc";
}

// Interface pour le résultat de recherche d'établissement
export interface EstablishmentSearchResult {
  id: string;
  slug: string;
  name: string;
  city: string | null;
  region: string | null;
  /** Code postal — alimente le `PostalAddress` JSON-LD des listings (sans rue). */
  postalCode?: string | null;
  latitude: number | null;
  longitude: number | null;
  description: string | null;
  descriptionEn?: string | null;
  heroPhoto: {
    url: string;
    alt: string | null;
  } | null;
  photoCount: number;
  /**
   * Prix par nuit le plus bas parmi les chambres de l'établissement.
   * - Si `priceContextualized` est false/undefined : prix de base statique minimum.
   * - Si `priceContextualized` est true : tarif nuitée contextuel pour la chambre la moins
   *   chère accueillant les occupants demandés, avec résolution saison + occupants.
   * Le tri et les filtres restent cohérents car tous les résultats d'une même requête
   * partagent le même mode de contextualisation.
   */
  priceFrom: number | null;
  /** true si le prix "à partir de" provient uniquement de chambres individuelles (maxAdults=1) */
  priceFromSingleOnly?: boolean;
  totalCapacity: number;
  totalAdults: number;
  totalChildren: number;
  hasTableDhotes: boolean;
  tableDhotesPrice: number | null;
  /** Prix repas table d'hôtes le plus bas parmi les tranches d'âge (pour badge "à partir de X€/pers") */
  tableDhotesPriceFrom?: number | null;
  equipments: readonly string[] | string[];
  paidServices?: Record<string, { enabled: boolean, price?: number | null, priceType?: string | null }> | null;
  roomCount: number;
  availableRooms?: number;
  totalRooms?: number;
  /**
   * Places adultes des seules chambres LIBRES aux dates demandées (chambres
   * d'hôtes datées uniquement). Le filtre de capacité le préfère à
   * `totalAdults` quand il est présent.
   */
  availableAdults?: number;
  /** Places totales (adultes + enfants) des seules chambres libres (chambres d'hôtes datées uniquement). */
  availableCapacity?: number;
  /** Nombre de chambres couvertes par `priceFrom` quand il vient d'une répartition multi-chambres. */
  priceRoomCount?: number;
  /**
   * true quand aucune chambre libre seule n'accueille le groupe et qu'aucune
   * répartition ne donne de prix (tarif manquant, nuits minimum non atteintes) :
   * `priceFrom` vaut alors `null` et la carte affiche « Prix sur demande ».
   */
  priceUncomputable?: boolean;
  /** Distance en km depuis le point de recherche (présent uniquement lors d'une recherche par ville/coordonnées) */
  distance?: number | null;
  /** Date d'inscription du propriétaire. Peut être Date ou string ISO après sérialisation JSON */
  ownerMemberSince: Date | string;
  /** Code-slug du département pour le routing URL (ex: "24-dordogne"). Calculé côté serveur depuis le code postal. */
  departmentCodeSlug?: string | null;
  /**
   * Nom du département en clair (ex: "Dordogne", "Lot"). Dérivé côté serveur depuis
   * `departmentCodeSlug` via `getDepartmentBySlug()`. `null` si pas de code postal valide
   * en BDD ou slug introuvable dans le mapping. Consommé par l'eyebrow des cartes V3
   * (`useEstablishmentEyebrow`) pour proposer une localisation plus précise que la région.
   */
  departmentName: string | null;
  /**
   * Variant de la carte V3 selon `photoCount` (Redesign V3, Vague 03).
   * - `'A'` : ≥ 3 photos (standard)
   * - `'B'` : 1 ou 2 photos (photo floutée + vignette)
   * - `'C'` : 0 photo (carte typographique)
   * Consommé par `PropertyCardV3.vue`. Calculé côté serveur via `getCardVariant`.
   */
  cardVariant: 'A' | 'B' | 'C';
  /**
   * Numéro court stable 3 chiffres (hash déterministe depuis `id`, ex: `'042'`).
   * Utilisé dans l'eyebrow éditorial `N°042 · DORDOGNE` des cartes V3 et
   * plus tard dans les pins numérotés de la carte résultats (Vague 04).
   * Purement décoratif, jamais utilisé comme clé fonctionnelle.
   */
  publicNumber: string;
  /** Note Google Places (1-5) */
  googleRating?: number | null;
  /** Nombre d'avis Google */
  googleReviewCount?: number | null;
  /** true si le prix est calculé pour les critères de recherche (dates + occupants) */
  priceContextualized?: boolean;
  /** Supplément enfant par nuit (présent uniquement en prix contextualisé avec enfants) */
  childRate?: number | null;
  /** Label de la meilleure réduction applicable (ex: "-10% dès 3 nuits") */
  stayDiscount?: string | null;
  /** Contrainte min nuitées de la chambre la moins contraignante (null = aucune) */
  minNightsConstraint?: number | null;
  /** Montant total de la taxe de séjour inclus dans le prix (en centimes, présent uniquement si > 0 et prix contextualisé) */
  touristTax?: number | null;
  /** Prix weekend par nuit (présent uniquement si weekendEnabled et prix contextualisé avec nuits WE) */
  weekendPriceFrom?: number | null;
  /** Nombre de nuits en semaine dans le séjour (présent uniquement si weekendEnabled et prix contextualisé) */
  weekdayNights?: number;
  /** Nombre de nuits weekend dans le séjour (présent uniquement si weekendEnabled et prix contextualisé) */
  weekendNights?: number;
  /**
   * Total du séjour en euros (story « total du séjour sur les cartes de gîtes »,
   * 4.131.0). GÎTE DATÉ UNIQUEMENT : hébergement après réductions + frais de
   * ménage, HORS taxe de séjour — exactement le `totalDue` du devis de la fiche
   * (`quote.get.ts`), avec les mêmes gardes (minimum de nuits de la saison
   * d'arrivée, total ≤ 0 traité comme indisponible). Absent sinon : la carte
   * retombe sur son prix par nuit. N'entre ni dans le filtre, ni dans le tri,
   * ni dans le compteur, qui restent sur `priceFrom`.
   */
  stayTotal?: number;
  /** Nombre de nuits couvertes par `stayTotal` (présent si et seulement si `stayTotal` l'est). */
  stayNights?: number;
  /**
   * Type de logement (chantier gîtes v4.0, Lot 9). Discriminant permettant à
   * l'UI (badges/onglets, Lot 11) et au routing de distinguer une chambre
   * d'hôtes d'un gîte. Défaut `'chambre-hotes'` pour ne casser aucun
   * consommateur existant du type.
   */
  accommodationType: AccommodationType;
}

// Interface pour les métadonnées de pagination
export interface SearchMeta {
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  /** Rayon effectif après élargissement automatique (présent si élargi) */
  expandedRadiusKm?: number;
}

// Point léger pour la carte (tous les résultats, sans pagination)
export interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  name: string;
  slug: string;
  departmentCodeSlug?: string | null;
  priceFrom: number | null;
  heroPhotoUrl: string | null;
  totalCapacity: number;
  city: string | null;
  hasTableDhotes: boolean;
  googleRating: number | null;
  googleReviewCount: number | null;
  roomCount: number;
  /**
   * Numéro court stable 3 chiffres (hash déterministe depuis `id`, ex: `'042'`).
   * Identique au `publicNumber` de `EstablishmentSearchResult` (ajouté Vague 03).
   * Consommé par les pins numérotés V3 de `SearchMapV3.vue` (Vague 04).
   */
  publicNumber: string;
  /** Type de logement (chantier gîtes v4.0, Lot 9) — pour pins différenciés (Lot 11). */
  accommodationType: AccommodationType;
}

/**
 * Stats agrégées sur l'ensemble des résultats filtrés (avant pagination).
 * Nourrit le contenu éditorial dynamique des pages pSEO long tail.
 * Retournée uniquement si `?includeStats=true` est passé à l'API.
 */
export interface FilterStats {
  /** Nombre total d'établissements matchés (= meta.total) */
  count: number;
  /** Top 3 villes par nombre d'établissements matchés */
  topCities: Array<{ city: string; count: number }>;
  /** Prix min/max/moyen en euros (prix statique, pas contextualisé) */
  priceRange: { min: number; max: number; avg: number } | null;
  /** Note Google moyenne (uniquement si ≥ 1 établissement avec rating) */
  avgRating: number | null;
  /** Nombre d'établissements avec au moins un avis Google */
  ratedCount: number;
}

// Interface pour la réponse de l'API de recherche
export interface SearchEstablishmentsResponse {
  data: EstablishmentSearchResult[];
  meta: SearchMeta;
  mapPoints: MapPoint[];
  /** Stats agrégées, présentes uniquement si `?includeStats=true` */
  filterStats?: FilterStats;
}

// Configuration carte recherche
export const MAP_CONFIG = {
  FRANCE_CENTER: { lat: 46.603354, lng: 1.888334 },
  DEFAULT_ZOOM: 7,
  SINGLE_RESULT_ZOOM: 12,
  MAX_FIT_ZOOM: 14,
  FIT_BOUNDS_PADDING: [50, 50] as [number, number],
  CLUSTER_THRESHOLD: 50,
  POPUP_MAX_WIDTH: 280,
  POPUP_MIN_WIDTH: 260,
  /** Sync viewport ↔ liste (Pattern C) — desktop only */
  VIEWPORT_DIVERGE_THRESHOLD: 0.4,
  VIEWPORT_REFRESH_DEBOUNCE_MS: 600,
  VIEWPORT_LOADMORE_LIMIT: 60,
} as const;

/** Bbox géographique (lng/lat NSEW) — utilisée pour la sync viewport ↔ liste. */
export interface MapBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

/**
 * Normalise le paramètre d'URL `type` en filtre d'hébergement exploitable.
 * Retourne `undefined` pour « Tout », l'absence de valeur ou une valeur invalide,
 * afin que le paramètre soit simplement omis des query strings reconstruites.
 */
export const ACCOMMODATION_TYPES = ['chambre-hotes', 'gite'] as const satisfies readonly AccommodationType[];

export function parseAccommodationTypeParam(
  raw: unknown,
): AccommodationType | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return (ACCOMMODATION_TYPES as readonly unknown[]).includes(value)
    ? (value as AccommodationType)
    : undefined;
}
